import {
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  effect,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, filter, take, takeUntil } from 'rxjs/operators';
import { FadedTextComponent } from '@app/framework/faded-text/faded-text.component';
import { MoneyPipe } from '@app/pipes/framework/money-short.pipe';
import { SearchInputComponent } from '@app/framework/search-input/search-input.component';
import { CORLogItem } from '@app/store/prime-commitments/prime-commitments.types';
import { ScrolledToBottomDirective } from '@app/directives/scrolled-to-bottom.directive';
import { primeDetailsSelectors } from '@app/store/prime-commitments/prime-commitments.selectors';
import { ChangeActionsStore } from '@app/store/change-actions/change-actions.store';
import { CheckboxComponent } from '@app/framework/inputs/checkbox/checkbox.component';
import { LoadingBarComponent } from '@app/framework/inputs/loading-bar/loading-bar.component';
import { CheckboxChangeEvent } from '@app/framework/interfaces/input.type';

@Component({
  selector: 'app-prime-change-order-cor-select',
  imports: [
    FadedTextComponent,
    FormsModule,
    MoneyPipe,
    SearchInputComponent,
    ScrolledToBottomDirective,
    NgClass,
    CheckboxComponent,
    LoadingBarComponent,
  ],
  templateUrl: './prime-change-order-cor-select.component.html',
  styleUrl: './prime-change-order-cor-select.component.scss',
})
export class PrimeChangeOrderCorSelectComponent implements OnInit, OnDestroy {
  @Output() selectedCORsChange = new EventEmitter<CORLogItem[]>();
  @Output() corTotalChange = new EventEmitter<number>();
  private readonly store = inject(Store);
  private readonly changeActionsStore = inject(ChangeActionsStore);

  protected readonly String = String;

  pccoDetails$ = this.store.select(primeDetailsSelectors.selectPCCOData);

  searchText$ = new Subject<string>();
  selectedCORIds = signal([]);
  allCORs = this.changeActionsStore.cors;
  corsLoading = this.changeActionsStore.corsLoading;

  isDestroyed$ = new Subject<void>();

  private effectRef = effect(() => {
    const selectedCORs = this.allCORs().filter((cor) => this.selectedCORIds().includes(cor.id));
    console.log('selected COR IDs', this.selectedCORIds(), selectedCORs, this.allCORs());
    this.selectedCORsChange.emit(selectedCORs);

    const sum = selectedCORs.reduce((acc, cor) => acc + cor.total_requested, 0);
    this.corTotalChange.emit(sum);
  });

  ngOnInit() {
    this.searchText$.pipe(takeUntil(this.isDestroyed$), debounceTime(300)).subscribe((text) => {
      this.changeActionsStore.corSearchTextChanged(text);
    });

    this.pccoDetails$
      .pipe(
        takeUntil(this.isDestroyed$),
        filter((data) => !!data),
        take(1),
      )
      .subscribe((pccoDetails) => {
        this.selectedCORIds.set(pccoDetails.cors.map((cor) => cor.id));
      });
  }

  ngOnDestroy() {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
  }

  onSearch($event: string) {
    this.searchText$.next($event);
  }

  toggleCOR(cor: CORLogItem) {
    if (this.selectedCORIds().includes(cor.id)) {
      this.selectedCORIds.update((ids) => ids.filter((id) => id !== cor.id));
    } else {
      this.selectedCORIds.update((ids) => [...ids, cor.id]);
    }
  }

  toggleAllCORs(checkbox: CheckboxChangeEvent) {
    if (!checkbox.checked) {
      this.selectedCORIds.set([]);
      return;
    }

    const allCORIds = this.allCORs().map((cor) => cor.id);
    this.selectedCORIds.set(allCORIds);
  }

  scrolledToBottom(event: Event) {
    this.changeActionsStore.loadCORs(false);
  }
}
